import type { RecordIssueRequest } from "@/lib/api";
import { spellCount, spellCountLower } from "@/lib/spell-count";

import { PRODUCT_NAMES } from "./product-names";

/**
 * What the app says, and files, when the hub never comes up.
 *
 * The hub is the one step nothing else works without: no compartment after it
 * can be provisioned, mapped or read. So the connecting screen does not offer
 * a way past it the way the connector screens do. After ten minutes it stops
 * pretending to wait, tells the resident VELUX will reach out, and files the
 * problem as urgent so the report leaves at once instead of waiting for the
 * hourly sweep.
 *
 * Pure and separate from the screen for the same reason `connector-issue.ts`
 * is: the copy this produces is read by support, and it is worth testing
 * without a component-testing stack.
 */

/** How the report names the fault. Keyed on by the backend's issue rows. */
export const HUB_UNREACHABLE_VERDICT = "hub_unreachable";

/**
 * What went wrong, in one sentence, for the support report.
 *
 * Fixed rather than assembled from whatever the poll last said: the poll's own
 * detail is a raw Home Assistant string and it goes in `what_was_tried` below,
 * where its shape does not matter. This line is the headline support sorts on,
 * so it reads the same every time.
 */
export const HUB_UNREACHABLE_DETAIL = `The ${PRODUCT_NAMES.hub} could not be reached after ten minutes on the connecting screen.`;

interface HubWaitState {
  /** Checks ticked off before the wait was called. */
  doneTasks: number;
  /** Checks on the list, so the sentence says how far it got. */
  totalTasks: number;
  /** The last thing the status poll said, if it said anything. */
  lastDetail?: string | null;
}

/**
 * How far the connecting screen got, for the report's "what was tried".
 *
 * Written for whoever picks the report up, not for the resident: the two
 * things that tell a hub which never booted apart from one the app could not
 * reach are how many checks passed and what the last poll came back with.
 *
 * Counts are spelled out per the copy rules. Support reads these beside
 * compartment numbers, and a digit next to "checks" reads as a label.
 */
export function describeHubWaitState({ doneTasks, totalTasks, lastDetail }: HubWaitState): string {
  const progress =
    doneTasks === 0
      ? `None of the ${spellCountLower(totalTasks)} checks completed.`
      : `${spellCount(doneTasks)} of ${spellCountLower(totalTasks)} checks completed.`;
  if (!lastDetail) {
    return `${progress} The hub check came back with no detail to go on.`;
  }
  return `${progress} Last status: ${endSentence(lastDetail)}`;
}

/**
 * The unreachable hub, as the issue report wants it.
 *
 * `urgent` is the point of this record. Everything else the app files waits
 * for the end of the flow or the hourly sweep, because the household can carry
 * on meanwhile. Here they cannot, so the report goes out while somebody can
 * still do something about it the same day.
 */
export function hubUnreachableRecord(state: HubWaitState): RecordIssueRequest {
  return {
    step_key: "home_assistant",
    stage: "connecting",
    verdict: HUB_UNREACHABLE_VERDICT,
    detail: HUB_UNREACHABLE_DETAIL,
    what_was_tried: describeHubWaitState(state),
    urgent: true,
  };
}

/**
 * A caption that trails off, once the waiting is over.
 *
 * The checklist's captions end in an ellipsis because they describe something
 * still happening. Beside a red cross that is no longer true, so the ellipsis
 * becomes a full stop rather than being dropped: these are sentences, and one
 * ending on a bare word reads like it was cut off mid-render.
 */
export function settledCaption(caption: string): string {
  return caption.replace(/\s*(\.\.\.|…)$/, ".");
}

/** Close a raw status string off, so the report reads as prose. */
function endSentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}
